/**
 * Fyre Hero Banner slider.
 *
 * A small, self-contained slider with a true opacity crossfade ("fade") and a
 * real translateX transition ("slide"). Autoplay animates with the same easing
 * as manual navigation (the theme's shared slider jumps instantly on autoplay).
 */
if (!customElements.get("fyre-hero-slider")) {
  class FyreHeroSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(".fyre-hero-banner__track");
      this.slides = Array.from(
        this.querySelectorAll(".fyre-hero-banner__slide")
      );
      if (!this.track || this.slides.length <= 1) return;

      this.effect = this.dataset.effect === "slide" ? "slide" : "fade";
      this.loop = this.dataset.loop === "true";
      this.autoplayMs = parseInt(this.dataset.autoplay, 10) || 0;
      this.index = 0;

      this.dots = Array.from(this.querySelectorAll("[data-fyre-dot]"));
      this.prevBtn = this.querySelector("[data-fyre-prev]");
      this.nextBtn = this.querySelector("[data-fyre-next]");

      this.reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      this.prevBtn?.addEventListener("click", () => this.step(-1));
      this.nextBtn?.addEventListener("click", () => this.step(1));
      this.dots.forEach((dot, i) =>
        dot.addEventListener("click", () => {
          this.go(i);
          this.restart();
        })
      );

      // Pause on hover / focus and when the tab is hidden.
      this.addEventListener("mouseenter", () => this.stop());
      this.addEventListener("mouseleave", () => this.start());
      this.addEventListener("focusin", () => this.stop());
      this.addEventListener("focusout", () => this.start());
      document.addEventListener("visibilitychange", () => {
        document.hidden ? this.stop() : this.start();
      });

      // Theme editor: follow block selection.
      if (window.Shopify && window.Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => {
          const slide = event.target.closest(".fyre-hero-banner__slide");
          const i = this.slides.indexOf(slide);
          if (i >= 0) {
            this.stop();
            this.go(i);
          }
        });
        this.addEventListener("shopify:block:deselect", () => this.start());
      }

      this.go(0, true);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    step(direction) {
      this.go(this.index + direction);
      this.restart();
    }

    go(target, instant) {
      const count = this.slides.length;

      if (this.loop) {
        this.index = ((target % count) + count) % count;
      } else {
        this.index = Math.max(0, Math.min(target, count - 1));
      }

      if (this.effect === "slide") {
        if (instant || this.reduceMotion) this.track.style.transition = "none";
        this.track.style.transform = `translateX(-${this.index * 100}%)`;
        if (instant || this.reduceMotion) {
          // Force reflow, then restore the transition for subsequent moves.
          void this.track.offsetWidth;
          this.track.style.transition = "";
        }
      }

      this.slides.forEach((slide, i) =>
        slide.classList.toggle("is-active", i === this.index)
      );
      this.dots.forEach((dot, i) =>
        dot.classList.toggle("is-active", i === this.index)
      );

      if (!this.loop) {
        if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
        if (this.nextBtn) this.nextBtn.disabled = this.index === count - 1;
      }
    }

    start() {
      if (this.reduceMotion || this.autoplayMs <= 0 || this._timer) return;
      this._timer = setInterval(() => this.go(this.index + 1), this.autoplayMs);
    }

    stop() {
      clearInterval(this._timer);
      this._timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  customElements.define("fyre-hero-slider", FyreHeroSlider);
}
